import React from 'react';
import { useLocation, useParams } from '@tanstack/react-router';
import { bindActionCreators } from 'redux';

import { connect, ConnectedProps } from 'react-redux';
import { TFunction } from 'react-i18next';
import debounce from 'lodash/debounce';
import {
  StyledContainer,
  Stack,
  List,
  Pagination,
  TextField,
  withTranslation,
} from '@piwikpro/ui-components';
import { ListPreference } from '@piwikpro/user-preferences';
import {
  Pagination as PaginationManager,
  view,
  withAccess,
  errorBoundary,
  withInject,
  ConfigService,
  QueryParams,
  InternalListLink,
  PermissionSelect,
} from '@piwikpro/platform';
import {
  fetchMetaSitePermissionsForUserGroups as fetchMetaSitePermissionsForUserGroupsMethod,
  MetaSitesState,
  updateMetaSitePermissionsForUserGroup as updateMetaSitePermissionsForUserGroupMethod,
} from '@piwikpro/meta-sites-crate/reducers';
import { DebouncedFunc } from 'lodash';
import { RouterService, buildPaginatedPath } from '@piwikpro/router-crate';
import { permissionSelectOptions } from '../../../../permissionOptions';

const mapStateToProps = (state: MetaSitesState) => ({
  metaSite: state.metaSites.details.settings.settings,
  isLoading: state.metaSites.details.permissions.userGroups.isLoading,
  permissions: state.metaSites.details.permissions.userGroups.permissions,
  total: state.metaSites.details.permissions.userGroups.total,
  failed: state.metaSites.details.permissions.userGroups.failed,
});

const mapDispatchToProps = dispatch => ({
  fetchMetaSitePermissionsForUserGroups: bindActionCreators(
    fetchMetaSitePermissionsForUserGroupsMethod,
    dispatch,
  ),
  updateMetaSitePermissionsForUserGroup: bindActionCreators(
    updateMetaSitePermissionsForUserGroupMethod,
    dispatch,
  ),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  config: ConfigService
  userPreferences: any
  metaSitePermissionLevels: any
  Title: any
  routerService: RouterService
}

interface Props extends ReduxProps, InjectedProps {
  routeParams: QueryParams
  pathname: string
  t: TFunction
}

interface State {
  searchDebounce: null | DebouncedFunc<() => void>
  search: string
}

class UserGroups extends React.Component<Props, State> {
  state = {
    searchDebounce: null,
    search: '',
  }

  constructor(props) {
    super(props);

    this.handleLimitChange = this.handleLimitChange.bind(this);
    this.handlePageChange = this.handlePageChange.bind(this);
    this.renderPermissionsRow = this.renderPermissionsRow.bind(this);
    this.handleUpdatePermissions = this.handleUpdatePermissions.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
  }

  async componentDidMount() {
    await this.fetchPermissions();
  }

  async componentDidUpdate(prevProps: Props) {
    const { routeParams } = this.props;

    if (
      routeParams.page !== prevProps.routeParams.page
      || routeParams.limit !== prevProps.routeParams.limit
    ) {
      await this.fetchPermissions();
    }
  }

  async fetchPermissions() {
    const { fetchMetaSitePermissionsForUserGroups, metaSite, routeParams } = this.props;
    const { search } = this.state;
    const pagination = PaginationManager.fromQueryParams(
      [],
      0,
      routeParams,
    );

    await fetchMetaSitePermissionsForUserGroups(metaSite.id, {
      search,
      limit: pagination.getLimit(),
      offset: pagination.offset,
    });
  }

  async handleLimitChange(limit) {
    const {
      routerService,
      pathname,
      fetchMetaSitePermissionsForUserGroups,
      userPreferences,
      metaSite,
    } = this.props;
    const listPreference = new ListPreference(userPreferences.load(), '');
    const { search } = this.state;

    listPreference.limit = limit;

    userPreferences.persist(listPreference);

    await fetchMetaSitePermissionsForUserGroups(metaSite.id, {
      search,
      limit,
      offset: 0,
    });

    routerService.push({ to: buildPaginatedPath(pathname, 1, limit) });
  }

  async handlePageChange(offset, pagination) {
    const { search } = this.state;
    const {
      routerService,
      pathname,
      fetchMetaSitePermissionsForUserGroups,
      metaSite,
    } = this.props;

    await fetchMetaSitePermissionsForUserGroups(metaSite.id, {
      search,
      limit: pagination.getLimit(),
      offset,
    });

    routerService.push({
      to: buildPaginatedPath(
        pathname,
        pagination.getPageFromOffset(offset),
        pagination.getLimit(),
      ),
    });
  }

  handleSearchChange(event) {
    const { searchDebounce } = this.state;
    const {
      fetchMetaSitePermissionsForUserGroups,
      metaSite,
      routeParams,
      routerService,
      pathname,
      config,
    } = this.props;
    const pagination = PaginationManager.fromQueryParams(
      [],
      0,
      routeParams,
    );

    if (searchDebounce) {
      (searchDebounce as DebouncedFunc<() => void>).cancel();
    }

    const handleSearch = debounce(() => {
      const { search } = this.state;

      fetchMetaSitePermissionsForUserGroups(metaSite.id, {
        search,
        offset: pagination.offset,
        limit: pagination.getLimit(),
      });
      routerService.push({ to: buildPaginatedPath(pathname, 1, pagination.getLimit()) });
    }, config.get('LIST_SEARCH_DELAY'));

    this.setState({
      searchDebounce: handleSearch,
      search: event.target.value,
    });

    handleSearch();
  }

  async handleUpdatePermissions(id, values, { setSubmitting, resetForm }) {
    const { updateMetaSitePermissionsForUserGroup, metaSite } = this.props;

    try {
      await updateMetaSitePermissionsForUserGroup(metaSite.id, id, values);
    } catch (err) {
      resetForm();
    }

    setSubmitting(false);
  }

  renderPermissionsRow(userGroup) {
    const { metaSitePermissionLevels, t } = this.props;
    const userGroupAccess = userGroup.attributes.permission;

    return (
      <List.Item
        key={userGroup.id}
      >
        <InternalListLink
          to="/administration/user-groups/$groupId"
          params={{ groupId: userGroup.id }}
          label={userGroup.attributes.name}
        />
        <PermissionSelect
          initialValue={userGroupAccess}
          options={permissionSelectOptions(metaSitePermissionLevels, t, 'meta-site')}
          onSubmit={(values, api) => (this.handleUpdatePermissions(
            userGroup.id,
            values,
            api,
          ))}
        />
      </List.Item>
    );
  }

  render() {
    const {
      permissions,
      isLoading,
      failed,
      routeParams,
      t,
      config,
      total,
      Title,
    } = this.props;
    const {
      search,
    } = this.state;
    const pagination = PaginationManager.fromQueryParams(
      permissions,
      total,
      routeParams,
    );

    return (
      <Title part={t('meta-sites.details.title.permissions.user-groups')}>
        <Stack.Item fill>
          {!failed && (
            <StyledContainer
              border={['left', 'right', 'top']}
              padding="narrow"
              backgroundColor="gray"
            >
              <TextField
                value={search}
                name="search"
                icon="search"
                onChange={this.handleSearchChange}
                placeholder={t('meta-sites.permissions.user-groups.list.search-placeholder')}
                clearable
              />
            </StyledContainer>
          )}
          <List
            columns={[
              {
                size: 4,
                align: 'left',
              },
              {
                size: 1,
                align: 'right',
              },
            ]}
            isLoading={isLoading}
            error={failed}
          >
            {
              !failed && permissions
                .map(userGroupWithPermissions =>
                  this.renderPermissionsRow(userGroupWithPermissions))}
          </List>
        </Stack.Item>
        {!failed && (
          <Stack.Item fill>
            <Pagination
              onLimitChange={this.handleLimitChange}
              limit={pagination.getLimit()}
              total={total}
              limitOptions={config.get('PAGINATION_ITEMS_PER_PAGE_OPTIONS')}
              startIndex={pagination.offset}
              endIndex={pagination.offset + pagination.getLimit()}
              onPageChange={e => this.handlePageChange(e, pagination)}
            />
          </Stack.Item>
        )}
      </Title>
    );
  }
}

const UserGroupsConnected = connector(withInject<InjectedProps>({
  config: 'config',
  userPreferences: 'userPreferences',
  metaSitePermissionLevels: 'metaSitePermissionLevels',
  Title: 'TitleCrate.components.Title',
  routerService: 'RouterCrate.router',
})(
  view({ role: 'OWNER' }, false)(withAccess(withTranslation(['administration'])(
    errorBoundary(UserGroups),
  ))),
));

const UserGroupsWrapper = (props) => {
  const routeParams = useParams({
    from: '/_secured/administration/meta-sites/$metaSiteId/permissions/user-groups/$page/$limit',
  });
  const { pathname } = useLocation();

  return (
    <UserGroupsConnected
      {...props}
      routeParams={routeParams}
      pathname={pathname}
    />
  );
};

export default UserGroupsWrapper;
